import React, { useMemo } from 'react';
import styled from 'styled-components';
import imgClose from '../../images/common/i_close_btn.svg';

const ContentWrap = styled.div`
    display: block;
    width: 100%;
    background: #fff;
`;

const InnerBox = styled.div`
    position: relative;

    display: flex;
    flex-direction: column;
    align-items: flex-start;

    width: 100%;
    padding: 2rem 1.6rem 2.4rem;

    .close-btn {
        position: absolute;
        top: 1.6rem;
        right: 1.6rem;
        z-index: 10;

        width: 2.4rem;
        height: 2.4rem;

        border: 0;
        padding: 0;

        background: url(${imgClose}) no-repeat center;
        background-size: 100%;

        cursor: pointer;
    }
`;

const TitleBox = styled.strong`
    display: block;
    width: calc(100% - 4rem);

    padding-bottom: 1.6rem;

    font-size: 2rem;
    line-height: 1.4;
    font-weight: 700;
    color: #10141A;

    word-break: keep-all;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: #E2E6EC;
`;

const ContentBox = styled.div`
    width: 100%;
    margin-top: 2rem;

    overflow-x: hidden;

    padding-right: 0.4rem;

    color: #4A5564;

    font-size: 1.4rem;
    line-height: 1.75;

    word-break: keep-all;
    overflow-wrap: break-word;

    /* ============================= */
    /* 스크롤바 */
    /* ============================= */

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #D9DEE6;
        border-radius: 10px;
    }

    /* ============================= */
    /* Word HTML 초기화 */
    /* ============================= */

    p,
    div,
    span {
        max-width: 100%;
    }

    p {
        margin: 0 0 1rem;
        padding: 0;

        font-size: 1.4rem;
        line-height: 1.75;
        color: #4A5564;
    }

    /* Word에서 자주 생성되는 빈 문단 */
    p:empty {
        min-height: 0.8rem;
        margin: 0;
    }

    /* ============================= */
    /* 대제목 */
    /* 제1장 총칙 등 */
    /* ============================= */

    h1 {
        margin: 3.2rem 0 1.6rem;

        font-size: 2rem;
        line-height: 1.45;
        font-weight: 700;
        color: #10141A;
    }

    h1:first-child {
        margin-top: 0;
    }

    /* ============================= */
    /* 중제목 */
    /* 제1조 (목적) 등 */
    /* ============================= */

    h2 {
        position: relative;

        margin: 2.8rem 0 1.2rem;
        padding-bottom: 0.8rem;

        font-size: 1.7rem;
        line-height: 1.5;
        font-weight: 700;
        color: #10141A;

        border-bottom: 1px solid #EDF0F4;
    }

    h2:first-child {
        margin-top: 0;
    }

    h3 {
        margin: 2.4rem 0 1rem;

        font-size: 1.6rem;
        line-height: 1.5;
        font-weight: 700;
        color: #20262E;
    }

    h4,
    h5,
    h6 {
        margin: 2rem 0 0.8rem;

        font-size: 1.5rem;
        line-height: 1.5;
        font-weight: 700;
        color: #20262E;
    }

    /* ============================= */
    /* 강조 */
    /* ============================= */

    strong,
    b {
        font-weight: 700;
        color: #20262E;
    }

    a {
        color: var(--color-main-5);
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    /* ============================= */
    /* 목록 */
    /* ============================= */

    ul,
    ol {
        margin: 1rem 0 1.6rem;
        padding-left: 2.4rem;
    }

    ul {
        list-style: disc;
    }

    ol {
        list-style: decimal;
    }

    li {
        margin: 0.5rem 0;

        font-size: 1.4rem;
        line-height: 1.75;
        color: #4A5564;
    }

    li > p {
        margin-bottom: 0.4rem;
    }

    /* ============================= */
    /* 표 */
    /* ============================= */

    .table-wrapper {
        width: 100%;

        margin: 2rem 0;

        overflow-x: auto;
        overflow-y: hidden;

        border: 1px solid #DCE1E8;
        border-radius: 0.8rem;

        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            height: 12px;
        }

        &::-webkit-scrollbar-track {
            background: #F7F8FA;
        }

        &::-webkit-scrollbar-thumb {
            background: #CDD3DC;
            border-radius: 8px;
            border: 3px solid #F7F8FA;
        }
    }

    table {
        width: 100%;

        min-width: 600px;

        border-collapse: collapse;
        border-spacing: 0;

        table-layout: auto;

        background: #fff;

        font-size: 1.3rem;
        line-height: 1.6;
    }

    thead {
        background: #F5F7FA;
    }

    th {
        padding: 1.2rem 1rem;

        background: #F5F7FA;

        font-weight: 700;
        text-align: center;
        vertical-align: middle;

        color: #20262E;

        border-right: 1px solid #E2E6EC;
        border-bottom: 1px solid #DCE1E8;

        white-space: nowrap;
    }

    td {
        padding: 1.2rem 1rem;

        color: #4A5564;

        vertical-align: middle;
        text-align: left;

        border-right: 1px solid #E2E6EC;
        border-bottom: 1px solid #E2E6EC;

        line-height: 1.65;
    }

    th:last-child,
    td:last-child {
        border-right: 0;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    tbody tr:nth-child(even) {
        background: #FAFBFC;
    }

    /*
     * Word → HTML 과정에서 td 안에 p가 들어가는 경우가 많음
     */
    td p,
    th p {
        margin: 0;

        font-size: inherit;
        line-height: inherit;
        color: inherit;
    }

    /* ============================= */
    /* 구분선 */
    /* ============================= */

    hr {
        height: 1px;

        margin: 2.4rem 0;

        border: 0;
        background: #E2E6EC;
    }

    /* ============================= */
    /* Word 특유 스타일 보정 */
    /* ============================= */

    .MsoNormal {
        margin: 0 0 1rem !important;

        font-family: inherit !important;
        font-size: inherit !important;
        line-height: 1.75 !important;
    }

    /* ============================= */
    /* 모바일 */
    /* ============================= */

    @media (max-width: 480px) {
        font-size: 1.4rem;

        h1 {
            font-size: 1.8rem;
        }

        h2 {
            font-size: 1.6rem;
        }

        h3 {
            font-size: 1.5rem;
        }

        table {
            min-width: 560px;
        }

        th,
        td {
            padding: 1rem 0.8rem;
        }
    }
`;

const AgreeConditionsBox = ({
    title,
    onClose,
    children
}) => {

    const processedHtml = useMemo(() => {

        if (!children) {
            return '';
        }

        let html = children;

        /*
         * table을 가로 스크롤 wrapper로 감싸기
         */
        html = html.replace(
            /<table\b([^>]*)>([\s\S]*?)<\/table>/gi,
            (match, attributes, contents) => {

                // 이미 wrapper로 감싸진 경우 중복 방지
                return `
                    <div class="table-wrapper">
                        <table${attributes}>
                            ${contents}
                        </table>
                    </div>
                `;
            }
        );

        return html;

    }, [children]);

    return (
        <ContentWrap>
            <InnerBox>

                <TitleBox>
                    {title || '약관 동의'}
                </TitleBox>

                <Divider />

                <ContentBox
                    dangerouslySetInnerHTML={{
                        __html: processedHtml
                    }}
                />

                {/* <button
                    type="button"
                    className="close-btn"
                    onClick={onClose}
                >
                    <span className="blind">
                        팝업 닫기
                    </span>
                </button> */}

            </InnerBox>
        </ContentWrap>
    );
};

export default AgreeConditionsBox;